/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useEffect, useRef } from 'react';
import { getYouTubeId } from '../utils/youtube';

export default function VideoFrame({
  url,
  title = 'Freight Services Fiji video',
  background = false,
  autoplay = false,
  loop = false,
  muted = false,
  controls = true,
  unmuteOnInteraction = false,
}) {
  const videoId = getYouTubeId(url);
  const iframeRef = useRef(null);
  const soundRequestRef = useRef(null);
  const shouldStartMuted = muted || (autoplay && unmuteOnInteraction);
  const originParam = typeof window !== 'undefined' ? 'origin=' + encodeURIComponent(window.location.origin) : '';

  const sendPlayerCommand = (func, args = []) => {
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
  };

  const primeMutedPlayback = () => {
    if (!autoplay || !unmuteOnInteraction) {
      return;
    }

    [0, 350, 900].forEach((delay) => {
      window.setTimeout(() => {
        sendPlayerCommand('mute');
        sendPlayerCommand('setVolume', [0]);
        sendPlayerCommand('playVideo');
      }, delay);
    });
  };

  useEffect(() => {
    if (!autoplay || !unmuteOnInteraction) {
      return undefined;
    }

    let soundRequested = false;
    const retryTimers = [];
    const interactionEvents = ['pointerdown', 'click', 'touchstart', 'keydown', 'wheel'];
    const removeInteractionListeners = () => {
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, enableSound));
    };

    const enableSound = () => {
      if (!soundRequested) {
        soundRequested = true;
        removeInteractionListeners();
      }

      [0, 120, 350, 800, 1500, 2600].forEach((delay) => {
        retryTimers.push(window.setTimeout(() => {
          sendPlayerCommand('setVolume', [100]);
          sendPlayerCommand('unMute');
          sendPlayerCommand('playVideo');
        }, delay));
      });
    };

    soundRequestRef.current = enableSound;
    interactionEvents.forEach((eventName) => {
      const options = eventName === 'keydown' ? { once: true } : { once: true, passive: true };
      window.addEventListener(eventName, enableSound, options);
    });

    return () => {
      soundRequestRef.current = null;
      removeInteractionListeners();
      retryTimers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [autoplay, unmuteOnInteraction]);

  if (!videoId) {
    return null;
  }

  const params = background
    ? `autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&playsinline=1&modestbranding=1&rel=0`
    : [
        'rel=0',
        'modestbranding=1',
        'playsinline=1',
        'enablejsapi=1',
        originParam,
        controls ? 'controls=1' : 'controls=0',
        autoplay ? 'autoplay=1' : '',
        shouldStartMuted ? 'mute=1' : '',
        loop ? `loop=1&playlist=${videoId}` : '',
      ]
        .filter(Boolean)
        .join('&');
  const videoClassName = [
    'fsf-video',
    background ? 'fsf-video--background' : '',
    autoplay && !background ? 'fsf-video--autoplay' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={videoClassName}>
      <iframe
        ref={iframeRef}
        title={title}
        src={`https://www.youtube.com/embed/${videoId}?${params}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        onLoad={primeMutedPlayback}
      />
    </div>
  );
}
